/* Storefront enhancements. Core browsing, product forms and cart stay native to Shopify. */
(() => {
  const headerGroup = document.getElementById('header-group');
  if (headerGroup) {
    const updateHeight = () => {
      const header = headerGroup.querySelector('.cs-header');
      for (const target of [document.documentElement, document.body]) {
        target.style.setProperty('--header-height', `${header?.offsetHeight || 68}px`);
        target.style.setProperty('--header-group-height', `${headerGroup.offsetHeight}px`);
      }
    };
    new ResizeObserver(updateHeight).observe(headerGroup);
    updateHeight();
  }

  // UpCart opens its own drawer after a successful add. Dismiss the native chooser
  // when that drawer opens so its top-layer dialog cannot cover the cart.
  const syncCartDialog = () => {
    if (!document.body.classList.contains('upcartPopupShow')) return;
    const chooser = document.getElementById('quick-add-dialog');
    if (chooser?.querySelector('dialog[open]') && typeof chooser.closeDialog === 'function') chooser.closeDialog();
  };
  new MutationObserver(syncCartDialog).observe(document.body, { attributes: true, attributeFilter: ['class'] });

  const menu = document.querySelector('[data-cs-menu]');
  const closeMenu = (restoreFocus = false) => {
    if (!menu?.open) return;
    menu.open = false;
    if (restoreFocus) menu.querySelector('summary')?.focus();
  };
  document.querySelector('[data-cs-close-menu]')?.addEventListener('click', () => closeMenu(true));
  document.addEventListener('click', (event) => {
    if (menu?.open && !menu.contains(event.target)) closeMenu();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeMenu(true);
  });

  const dialog = document.getElementById('cs-search-dialog');
  const input = dialog?.querySelector('[data-cs-search-input]');
  const resultBox = dialog?.querySelector('[data-cs-search-results]');
  const status = dialog?.querySelector('[data-cs-search-status]');
  let searchTrigger;
  let timer;
  let controller;
  let searchSequence = 0;
  const root = window.Shopify?.routes?.root || '/';
  const formatPrice = (price) => {
    const value = Number(price);
    if (!Number.isFinite(value)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: window.Shopify?.currency?.active || 'USD',
      }).format(value);
    } catch { return ''; }
  };
  const closeSearch = () => dialog?.close();
  document.querySelectorAll('[data-cs-open-search]').forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      if (!dialog?.showModal) return;
      event.preventDefault();
      searchTrigger = trigger;
      closeMenu();
      dialog.showModal();
      input?.focus();
    });
  });
  dialog?.querySelector('[data-cs-close-search]')?.addEventListener('click', closeSearch);
  dialog?.addEventListener('close', () => {
    clearTimeout(timer);
    controller?.abort();
    searchTrigger?.focus();
  });
  dialog?.addEventListener('click', (event) => {
    if (event.target !== dialog) return;
    const box = dialog.getBoundingClientRect();
    if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closeSearch();
  });
  const suggest = async (query, sequence) => {
    controller = new AbortController();
    status.textContent = 'Searching…';
    try {
      const url = new URL(`${root}search/suggest.json`, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '5');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Search unavailable');
      const data = await response.json();
      if (sequence !== searchSequence) return;
      const products = data.resources?.results?.products || [];
      resultBox.replaceChildren();
      status.textContent = products.length ? `${products.length} suggested products` : 'No suggestions. Try another search, or press Enter to see all results.';
      const list = document.createElement('ul');
      list.className = 'cs-search-results';
      list.setAttribute('aria-label', 'Suggested products');
      for (const product of products) {
        const item = document.createElement('li');
        const link = document.createElement('a');
        const productUrl = new URL(product.url, location.origin);
        if (productUrl.origin !== location.origin) continue;
        link.href = productUrl.href;
        link.className = 'cs-search-result';
        if (product.image) {
          const image = document.createElement('img');
          const imageUrl = new URL(product.image, location.origin);
          if (imageUrl.protocol === 'https:') {
            imageUrl.searchParams.set('width', '120');
            image.src = imageUrl.href;
            image.alt = '';
            image.width = 60;
            image.height = 60;
            link.append(image);
          }
        }
        const text = document.createElement('span');
        text.textContent = product.title;
        const price = document.createElement('small');
        price.textContent = formatPrice(product.price);
        text.append(price);
        link.append(text);
        item.append(link);
        list.append(item);
      }
      resultBox.append(list);
    } catch (error) {
      if (error.name !== 'AbortError' && sequence === searchSequence) {
        status.textContent = 'Press Enter to search the full collection.';
      }
    }
  };
  input?.addEventListener('input', () => {
    clearTimeout(timer);
    controller?.abort();
    const sequence = ++searchSequence;
    const query = input.value.trim();
    resultBox.replaceChildren();
    status.textContent = '';
    if (query.length >= 2) timer = setTimeout(() => suggest(query, sequence), 250);
  });
  dialog?.addEventListener('keydown', (event) => {
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    const links = [...resultBox.querySelectorAll('a')];
    if (!links.length) return;
    const current = links.indexOf(document.activeElement);
    if (document.activeElement !== input && current === -1) return;
    event.preventDefault();
    if (event.key === 'ArrowUp' && current <= 0) input.focus();
    else links[event.key === 'ArrowDown' ? Math.min(current + 1, links.length - 1) : current - 1]?.focus();
  });

  // Correct the existing embed's label only inside this theme, preserving Klaviyo's form and consent flow.
  document.querySelectorAll('[data-cs-newsletter]').forEach((container) => {
    const fixLabel = () => {
      let corrected = false;
      container.querySelectorAll('input[type="email"]').forEach((email) => {
        email.placeholder = 'Email address';
      });
      container.querySelectorAll('button').forEach((button) => {
        const walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT);
        let textNode;
        while ((textNode = walker.nextNode())) {
          if (/\b(?:SUBCRIBE|Subscribe)\b/i.test(textNode.textContent)) {
            textNode.textContent = textNode.textContent.replace(/\b(?:SUBCRIBE|Subscribe)\b/gi, 'Sign up');
            corrected = true;
          }
        }
      });
      return corrected;
    };
    if (fixLabel()) return;
    const observer = new MutationObserver(() => { if (fixLabel()) observer.disconnect(); });
    observer.observe(container, { childList: true, subtree: true });
  });
})();
